import { onSnapshot, query } from "firebase/firestore";
import { useEffect, useState } from "react";
import { GetTasksInGroup } from "../FirebaseQueries";
import { Grid, Stack, Typography } from "@mui/material";
import { blue, green, orange, red } from "@mui/material/colors";
import RemoveIcon from '@mui/icons-material/Remove';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { Scenario } from "../Scenarios/Scenarios";
import { AnswersGroup } from "../Types";

interface Task {
	index: number;
	isReached: boolean;
	isFinished: boolean;
	isSolo: boolean;
	stageIndex: number;
	answerFinal: AnswersGroup[];
}

interface Props {
	groupId: string;
	scenario: Scenario | undefined;
}

export default function ClassProgressTasksRow(props: Props) {
	const { groupId, scenario } = props;
	const [tasks, setTasks] = useState<Task[]>([]);

	useEffect(() => { // Get Tasks
		const q = query(GetTasksInGroup(groupId));
		onSnapshot(q, (tasksSnapshot) => {
			let newTasks: Task[] = [];
			if (!tasksSnapshot.empty) {
				tasksSnapshot.forEach((doc) => {
					newTasks.push({
						index: doc.data().taskIndex,
						isReached: doc.data().reached,
						isFinished: doc.data().taskFinished,
						isSolo: doc.data().isSolo,
						stageIndex: doc.data().stageIndex,
						answerFinal: doc.data().answerFinal
					});
				});
			}
			else {
				// Do something if empty
			}
			setTasks(newTasks);
		});

	}, [groupId]);

	const isTaskCorrect = (index: number, answer: AnswersGroup[]): boolean => {
		const taskDef = scenario?.tasks.find((task) => task.taskIndex === index)
		if (taskDef === undefined) return false;

		return areAnswersSame(taskDef.correctAnswersGroups, answer);
	}

	const areAnswersSame = (answersA: AnswersGroup[], answersB: AnswersGroup[]): boolean => {
		if (answersA.length !== answersB.length) return false;

		for (let i = 0; i < answersA.length; i++) {
			if (answersA[i].indexes.length !== answersB[i].indexes.length) return false;

			for (const answerIndexA of answersA[i].indexes) {
				if (!answersB[i].indexes.some((value) => value === answerIndexA)) return false;
			}
		}

		return true;
	}

	const taskStatusBlock = (task: Task) => {
		if (task.isFinished) {
			if (task.isSolo) {
				return (<Stack
					height={1}
					justifyContent={"center"}
					alignItems={"center"}
					sx={{
						boxSizing: "border-box",
						border: "3px solid",
						borderColor: task.stageIndex === 0 ? blue[300] : orange[300],
						bgcolor: task.stageIndex === 0 ? blue[100] : orange[100],
						borderRadius: 1,
						opacity: 0.75,
						overflow: "hidden"
					}}>
					<Typography
						fontWeight={"bold"}
						fontSize={"0.8em"}
						sx={{ color: task.stageIndex === 0 ? blue[500] : orange[500] }}>
						Finished
					</Typography>
				</Stack>)
			} else {
				const isCorrect = isTaskCorrect(task.index, task.answerFinal);
				return (<Stack
					height={1}
					justifyContent={"center"}
					alignItems={"center"}
					sx={{
						boxSizing: "border-box",
						border: "3px solid",
						borderColor: isCorrect ? green[500] : red[500],
						bgcolor: task.stageIndex === 0 ? blue[100] : orange[100],
						borderRadius: 1,
						opacity: 0.75,
					}} >
					{isCorrect ?
						<DoneIcon sx={{ color: green[500] }}></DoneIcon> :
						<ClearIcon sx={{ color: red[500] }}></ClearIcon>
					}
				</Stack>)
			}

		} else {
			return (<Stack
				height={1}
				justifyContent={"center"}
				alignItems={"center"}
				sx={{
					boxSizing: "border-box",
					border: task.isReached ? "2px solid" : "2px dashed",
					borderColor: task.isReached ?
						task.stageIndex === 0 ? blue[400] : orange[400] :
						task.stageIndex === 0 ? blue[300] : orange[300],
					bgcolor: task.stageIndex === 0 ? blue[100] : orange[100],
					borderRadius: 1,
					opacity: task.isReached ? 0.5 : 0.25,
				}} >
				{task.isReached ?
					<MoreHorizIcon sx={{ color: task.stageIndex === 0 ? blue[400] : orange[400] }}></MoreHorizIcon>
					:
					<RemoveIcon sx={{ color: task.stageIndex === 0 ? blue[300] : orange[300] }}></RemoveIcon>
				}
			</Stack>)
		}
	}

	return (
		<Grid height={"40px"} container spacing={0.5}>
			{tasks.length > 0 ?
				<>
					{tasks.map((task, index) => (
						<Grid
							key={index}
							height={1}
							size={1}>
							{taskStatusBlock(task)}
						</Grid>
					))}
				</>
				:
				<>
					{scenario?.tasks.map((_task, index) => (
						<Grid
							key={index}
							height={1}
							size={1}>
						</Grid>
					))}
				</>
			}
		</Grid>
	);
}